import { useCallback, useEffect, useMemo, useState } from 'react';

const PER_PAGE = 15;

const normalizeUserPayload = (values = {}) => ({
  id: values.id,
  name: values.name || '',
  email: values.email || '',
  id_boh: values.id_boh ?? '',
  picture_url: values.picture_url || '',
  is_super_admin: Boolean(values.is_super_admin),
  activo: Boolean(values.activo),
  last_login_at: values.last_login_at || null,
  login_count: values.login_count || 0,
  created_at: values.created_at || null
});

export const useAdminUsers = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const fetchList = useCallback(async (signal) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: PER_PAGE.toString(),
        search: search || ''
      });
      const response = await fetch(`/admin/api/users?${params.toString()}`, { signal });
      const data = await response.json();
      if (!response.ok || data?.error) {
        throw new Error(data?.error || 'No se pudieron obtener los usuarios');
      }
      if (!signal?.aborted) {
        const normalized = (data.users || []).map((user) => normalizeUserPayload(user));
        setItems(normalized);
        setTotal(data.total || 0);
      }
    } catch (err) {
      if (!signal?.aborted) {
        setError(err?.message || 'Error al cargar usuarios');
      }
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, [page, search]);

  useEffect(() => {
    const controller = new AbortController();
    fetchList(controller.signal);
    return () => controller.abort();
  }, [fetchList, refreshToken]);

  const refresh = useCallback(() => {
    setRefreshToken((value) => value + 1);
  }, []);

  const setSearch = useCallback((value) => {
    setPage(1);
    setSearchValue(value);
  }, []);

  const toggleActive = useCallback(async (userId) => {
    const response = await fetch(`/admin/api/users/${userId}/toggle-active`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    });
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudo actualizar el usuario');
    }
    return data;
  }, []);

  const toggleAdmin = useCallback(async (userId) => {
    const response = await fetch(`/admin/api/users/${userId}/toggle-admin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    });
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudieron actualizar los privilegios');
    }
    return data;
  }, []);

  const loadSyncPreview = useCallback(async () => {
    const response = await fetch('/admin/api/users/sync-preview');
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudo obtener la sincronización');
    }
    return data;
  }, []);

  const syncUsers = useCallback(async (selection) => {
    const response = await fetch('/admin/api/users/sync-boh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ selected_users: selection })
    });
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudo completar la sincronización');
    }
    return data;
  }, []);

  const loadPermissionsPreview = useCallback(async () => {
    const response = await fetch('/admin/api/sucursales-users/sync-preview');
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudo obtener la sincronización de permisos');
    }
    return data;
  }, []);

  const syncPermissions = useCallback(async () => {
    const response = await fetch('/admin/api/sucursales-users/sync-boh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    });
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudo sincronizar los permisos');
    }
    return data;
  }, []);

  const createUser = useCallback(async (payload) => {
    const response = await fetch('/admin/api/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudo crear el usuario');
    }
    return data;
  }, []);

  const updateUser = useCallback(async (userId, payload) => {
    const response = await fetch(`/admin/api/users/${userId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudo actualizar el usuario');
    }
    return data;
  }, []);

  const loadUserRestaurants = useCallback(async (userId) => {
    const response = await fetch(`/admin/api/users/${userId}/restaurantes`);
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudieron obtener los restaurantes del usuario');
    }
    return data;
  }, []);

  return useMemo(() => ({
    items,
    total,
    page,
    perPage: PER_PAGE,
    loading,
    error,
    search,
    setPage,
    setSearch,
    refresh,
    toggleActive,
    toggleAdmin,
    createUser,
    updateUser,
    loadUserRestaurants,
    loadSyncPreview,
    syncUsers,
    loadPermissionsPreview,
    syncPermissions
  }), [
    items,
    total,
    page,
    loading,
    error,
    search,
    toggleActive,
    toggleAdmin,
    createUser,
    updateUser,
    loadUserRestaurants,
    loadSyncPreview,
    syncUsers,
    loadPermissionsPreview,
    syncPermissions
  ]);
};
